/**
 * Team Challenges — leaderboard & aggregate stats for teams.
 */
import { supabase } from '@/lib/supabase';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface TeamLeaderboardEntry {
  profile_id: string;
  display_name: string | null;
  handle: string | null;
  avatar_url: string | null;
  strength_score: number;
  rank: number;
}

export interface TeamStats {
  combined_strength: number;
  average_elo: number;
  workouts_this_week: number;
  member_count: number;
}

/* ------------------------------------------------------------------ */
/*  Queries                                                            */
/* ------------------------------------------------------------------ */

/**
 * Fetch all team members' strength scores, return ranked descending.
 */
export async function getTeamLeaderboard(teamId: string): Promise<TeamLeaderboardEntry[]> {
  // Get member profile IDs
  const { data: members, error: mErr } = await supabase
    .from('team_members')
    .select('profile_id, profiles(display_name, handle, avatar_url, duel_standard_strength_kg)')
    .eq('team_id', teamId);

  if (mErr || !members) return [];

  const entries: TeamLeaderboardEntry[] = members
    .map((m: any) => ({
      profile_id: m.profile_id,
      display_name: m.profiles?.display_name ?? null,
      handle: m.profiles?.handle ?? null,
      avatar_url: m.profiles?.avatar_url ?? null,
      strength_score: m.profiles?.duel_standard_strength_kg ?? 0,
      rank: 0,
    }))
    .sort((a, b) => b.strength_score - a.strength_score);

  entries.forEach((e, i) => { e.rank = i + 1; });
  return entries;
}

/**
 * Aggregate stats: combined strength, weekly workouts, average Elo.
 */
export async function getTeamStats(teamId: string): Promise<TeamStats> {
  // Get member profile IDs + strength
  const { data: members, error: mErr } = await supabase
    .from('team_members')
    .select('profile_id, profiles(duel_standard_strength_kg)')
    .eq('team_id', teamId);

  if (mErr || !members) {
    return { combined_strength: 0, average_elo: 0, workouts_this_week: 0, member_count: 0 };
  }

  const profileIds = members.map((m: any) => m.profile_id);
  const memberCount = profileIds.length;

  // Combined strength
  const combinedStrength = members.reduce(
    (sum: number, m: any) => sum + (m.profiles?.duel_standard_strength_kg ?? 0),
    0,
  );

  // Average Elo from elo_ratings
  let averageElo = 0;
  if (profileIds.length > 0) {
    const { data: eloData } = await supabase
      .from('elo_ratings')
      .select('profile_id, rating')
      .in('profile_id', profileIds);
    if (eloData && eloData.length > 0) {
      const totalElo = eloData.reduce((sum: number, e: any) => sum + (e.rating ?? 1200), 0);
      averageElo = Math.round(totalElo / eloData.length);
    }
  }

  // Workouts this week
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  let workoutsThisWeek = 0;
  if (profileIds.length > 0) {
    const { count } = await supabase
      .from('workouts')
      .select('id', { count: 'exact', head: true })
      .in('user_id', profileIds)
      .gte('performed_at', weekAgo.toISOString());
    workoutsThisWeek = count ?? 0;
  }

  return {
    combined_strength: Math.round(combinedStrength),
    average_elo: averageElo,
    workouts_this_week: workoutsThisWeek,
    member_count: memberCount,
  };
}
